import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { scrollStore } from '@/lib/scrollStore'

const SCROLL_THROTTLE_MS = 100
const MAX_RESTORE_FRAMES = 10

interface RestorationValue {
  mainRef: React.RefObject<HTMLElement>
  /** Jump to top. Called by useUrlState after a user-initiated filter change. */
  resetScroll: () => void
  /** Pages with an infinite list report their current page count here. */
  reportDepth: (depth: number) => void
  /** Hold the restore gate open while rehydrating; call the returned fn to release. */
  acquireGate: () => () => void
  /** Abandon the pending restore and land at the top instead. */
  cancelRestore: () => void
}

const RestorationContext = createContext<RestorationValue | null>(null)

export function useRestoration(): RestorationValue {
  const ctx = useContext(RestorationContext)
  if (!ctx) throw new Error('useRestoration must be used inside <RestorationProvider>')
  return ctx
}

export function RestorationProvider({ children }: { children: React.ReactNode }) {
  const mainRef = useRef<HTMLElement>(null)
  const depthRef = useRef(1)
  const location = useLocation()
  const navigationType = useNavigationType()

  const [gateCount, setGateCount] = useState(0)
  // The restore payload lives in a ref so clearing it does NOT re-render and
  // tear down the rAF reapply loop mid-flight. `restoreTick` is the trigger:
  // bumping it (batched with a page's gate acquisition) schedules the restore
  // effect without the effect depending on the payload it consumes.
  const pendingRestoreRef = useRef<number | null>(null)
  const [restoreTick, setRestoreTick] = useState(0)
  // Tracks the last restoreTick we acted on. The restore effect uses it to
  // ignore its initial mount run (every effect fires on mount regardless of
  // deps) and only restore once restoreTick has genuinely advanced.
  const lastRestoreTickRef = useRef(0)

  const prevPathnameRef = useRef(location.pathname)

  const reportDepth = useCallback((depth: number) => {
    depthRef.current = depth
  }, [])

  const acquireGate = useCallback(() => {
    setGateCount((n) => n + 1)
    let released = false
    return () => {
      if (released) return
      released = true
      setGateCount((n) => Math.max(0, n - 1))
    }
  }, [])

  const resetScroll = useCallback(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [])

  // A page that cannot rehydrate deeply enough (past MAX_RESTORE_DEPTH) says so here.
  // Without this the restore effect still drives toward the saved offset against a
  // one-page list, which the browser clamps to the BOTTOM — the opposite of landing
  // at the top, and it immediately trips the infinite-scroll observer.
  const cancelRestore = useCallback(() => {
    pendingRestoreRef.current = 0
  }, [])

  // Snapshot scroll (throttled) under the CURRENT location.key.
  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const onScroll = () => {
      if (timer) return
      timer = setTimeout(() => {
        timer = null
        scrollStore.set(location.key, {
          scrollTop: el.scrollTop,
          pageDepth: depthRef.current,
          pathname: location.pathname,
        })
      }, SCROLL_THROTTLE_MS)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      // FLUSH the pending write, don't discard it. Scrolling and then immediately
      // clicking a card lands the last scroll event inside the throttle window; a
      // clearTimeout alone would throw that final offset away and restore the user
      // to a stale position (or none at all, if this key was never written).
      if (timer) {
        clearTimeout(timer)
        scrollStore.set(location.key, {
          scrollTop: el.scrollTop,
          pageDepth: depthRef.current,
          pathname: location.pathname,
        })
      }
    }
  }, [location.key, location.pathname])

  // Navigation policy. See spec §Architecture unit 4.
  useEffect(() => {
    const pathnameChanged = prevPathnameRef.current !== location.pathname
    prevPathnameRef.current = location.pathname

    if (navigationType === 'POP') {
      // Discard a snapshot taken on a different pathname — `location.key` is reused
      // across documents ("default"), so it is not on its own a safe identity.
      const raw = scrollStore.get(location.key)
      const snap = raw && raw.pathname === location.pathname ? raw : undefined
      // No snapshot on a SAME-pathname entry means one we pushed but never scrolled —
      // the lightbox's `?event=` entry (the modal locks scroll, so nothing is ever
      // recorded against its key). Forcing 0 there would slam the feed to the top
      // behind the modal on a Forward press or a reload. Leave scroll untouched.
      // A cross-pathname POP with no snapshot still lands at the top, as it should.
      pendingRestoreRef.current = snap ? snap.scrollTop : pathnameChanged ? 0 : null
      // Bump a tick to run the restore effect. This setState batches with the
      // page's gate acquisition (its child effect runs in the same flush), so
      // the restore effect first observes gateCount>0 and waits for the seed.
      setRestoreTick((t) => t + 1)
      return
    }
    if (navigationType === 'PUSH' && pathnameChanged) {
      // Real page transition. Also fixes the pre-existing bug where <main>
      // (which never unmounts) kept the previous page's scrollTop.
      depthRef.current = 1
      resetScroll()
      pendingRestoreRef.current = null
      return
    }
    // PUSH with same pathname  → the event lightbox pushing ?event=; leave scroll.
    // REPLACE                  → useUrlState owns filter-driven scroll reset.
  }, [location.key, location.pathname, navigationType, resetScroll])

  // Apply the restore once every page has released the gate. Reapply across
  // frames because content height grows as rows mount and thumbnails load;
  // assigning past scrollHeight silently clamps.
  useEffect(() => {
    if (gateCount > 0) return
    // Ignore the initial mount run: every effect fires on mount regardless of
    // deps, and at that point the nav effect has set the ref while gateCount is
    // still the stale mount value 0 — restoring here would clamp to the bottom
    // of page 1 before a gated infinite-list page re-seeds. Only act once
    // restoreTick has advanced past the tick we last handled. Leaving the ref
    // intact when we bail (gate still held) lets the release re-run restore it.
    if (restoreTick === lastRestoreTickRef.current) return
    lastRestoreTickRef.current = restoreTick

    const target = pendingRestoreRef.current
    if (target === null) return
    const el = mainRef.current
    if (!el) return

    // Clearing the ref does not re-render, so this effect is NOT torn down
    // while the reapply loop below is in flight — that was the bug in the
    // earlier state-driven version (the effect cancelled its own frames).
    pendingRestoreRef.current = null
    if (target === 0) {
      el.scrollTop = 0
      return
    }

    let frame = 0
    let raf = 0
    const apply = () => {
      el.scrollTop = target
      frame++
      // Content height grows as rows mount and thumbnails load; assigning past
      // scrollHeight silently clamps, so reapply until tall enough or we hit
      // the frame budget.
      const tallEnough = el.scrollHeight >= target + el.clientHeight
      if (!tallEnough && frame < MAX_RESTORE_FRAMES) {
        raf = requestAnimationFrame(apply)
      }
    }
    raf = requestAnimationFrame(apply)
    return () => cancelAnimationFrame(raf)
  }, [restoreTick, gateCount])

  return (
    <RestorationContext.Provider value={{ mainRef, resetScroll, reportDepth, acquireGate, cancelRestore }}>
      {children}
    </RestorationContext.Provider>
  )
}
